<template>
  <div class="horizontal-scroll" :style="cssProps">
    <button
      v-if="contentOverflows && !atBeginning"
      class="arrow arrow-left"
      @click="scrollLeft"
    >
      <x-icon :height="arrowSizeValue" icon="chevron-left" />
    </button>

    <div
      ref="scrollContainer"
      class="scroll-container"
      @scroll.passive="checkScrollBoundaries"
    >
      <div ref="allContent" class="all-content" :class="listClasses">
        <slot />
      </div>
    </div>

    <button
      v-if="contentOverflows && !atEnd"
      class="arrow arrow-right"
      @click="scrollRight"
    >
      <x-icon :height="arrowSizeValue" icon="chevron-right" />
    </button>
  </div>
</template>

<script lang="ts">
/* eslint-disable vue/component-api-style */
export default {
  name: 'XHorizontalScroll',
}
export enum ChevronAlignments {
  Top = 'top',
  Center = 'center',
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, type StyleValue } from 'vue'

import xIcon from '@/components/shared/x-icon/index.vue'

type xHorizontalScrollProps = {
  fadeColor?: string
  scrollSize?: number
  arrowSize?: number
  chevronAlign?: ChevronAlignments
  gap?: number
}
const props = withDefaults(defineProps<xHorizontalScrollProps>(), {
  fadeColor: 'white',
  scrollSize: 50,
  arrowSize: 20,
  chevronAlign: ChevronAlignments.Center,
  gap: 8,
})

const cssProps = computed(() => {
  const chevronAlignment = {
    top: '0px',
    center: `calc(50% - ${props.arrowSize / 2}px)`,
  }[props.chevronAlign]
  return {
    '--fade-color': props.fadeColor,
    '--gap': `${props.gap}px`,
    '--chevron-top': chevronAlignment,
  } as StyleValue
})

const arrowSizeValue = computed(() => {
  return `${props.arrowSize}px`
})

const scrollContainer = ref<HTMLElement>()
const allContent = ref<HTMLElement>()
const contentOverflows = ref(false)
const atEnd = ref<boolean>(false)
const atBeginning = ref<boolean>(true)

/** Computed **/
const listClasses = computed<string[]>(() => {
  const classes = []

  if (!contentOverflows.value || atBeginning.value) {
    classes.push('hide-left-shade')
  }
  if (!contentOverflows.value || atEnd.value) {
    classes.push('hide-right-shade')
  }

  return classes
})

/** Functions **/
const handleResize = () => {
  contentOverflows.value = isContentOverflowing()
}

const checkScrollBoundaries = () => {
  if (
    scrollContainer.value?.scrollLeft === undefined ||
    scrollContainer.value?.scrollWidth === undefined ||
    scrollContainer.value?.offsetWidth === undefined
  ) {
    return
  }

  // check left side
  atBeginning.value = scrollContainer.value.scrollLeft === 0

  // check right side
  atEnd.value =
    Math.ceil(scrollContainer.value?.scrollLeft) >=
    scrollContainer.value?.scrollWidth - scrollContainer.value?.offsetWidth
}

const scrollLeft = () => {
  if (atBeginning.value || !scrollContainer.value) {
    return
  }

  scrollContainer.value.scroll({
    left: scrollContainer.value?.scrollLeft - props.scrollSize,
    behavior: 'smooth',
  })
  checkScrollBoundaries()
}

const scrollRight = () => {
  if (atEnd.value || !scrollContainer.value) {
    return
  }

  scrollContainer.value.scroll({
    left: scrollContainer.value?.scrollLeft + props.scrollSize,
    behavior: 'smooth',
  })
  checkScrollBoundaries()
}

const isContentOverflowing = () => {
  if (allContent.value?.clientWidth && scrollContainer.value?.clientWidth) {
    return allContent.value.clientWidth > scrollContainer.value.clientWidth
  }

  return false
}

/** Lifecycle **/
onMounted(() => {
  // watch for the visible content to resize and then recalculate whether the arrows
  // should be shown
  const resizeObs = new ResizeObserver(handleResize)
  resizeObs.observe(allContent.value as Element)
})
</script>

<style lang="scss" scoped>
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_type.scss';

.horizontal-scroll {
  position: relative;

  .scroll-container {
    overflow-x: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .all-content {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: var(--gap);
    flex-wrap: nowrap;
    white-space: nowrap;
    width: fit-content;

    &:before {
      content: '';
      position: absolute;
      z-index: 1;
      left: 0;
      top: 0;
      pointer-events: none;
      background-image: linear-gradient(
        to left,
        rgba(255, 255, 255, 0),
        var(--fade-color) 70%
      );
      width: 15%;
      height: 100%;
    }

    &:after {
      content: '';
      position: absolute;
      z-index: 1;
      right: 0;
      top: 0;
      pointer-events: none;
      background-image: linear-gradient(
        to right,
        rgba(255, 255, 255, 0),
        var(--fade-color) 70%
      );
      width: 15%;
      height: 100%;
    }

    &.hide-left-shade {
      &:before {
        content: none;
      }
    }

    &.hide-right-shade {
      &:after {
        content: none;
      }
    }
  }

  .arrow {
    z-index: 10;
    position: absolute;
    top: 10px;
    background-color: transparent;
    color: colors.$BaseBlack;
    padding: 0.1em 0.5em 0.35em;
    outline: none;
    border: none;
    cursor: pointer;

    svg {
      height: 16px;
    }

    &.hidden {
      display: none;
    }

    &.arrow-left {
      left: 4px;
      top: var(--chevron-top);
    }

    &.arrow-right {
      right: 4px;
      top: var(--chevron-top);
    }
  }
}
</style>
